import React from 'react';
import { Box, Divider, Typography } from '@mui/material';
import { DESIGN_TOKENS } from '../../design-system/tokens/colors';

function slugify(raw: string): string {
  const s = raw
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    .replace(/[`*_~]/g, '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return s || 'section';
}

function renderInline(text: string): React.ReactNode[] {
  const nodes: React.ReactNode[] = [];
  let i = 0;
  while (i < text.length) {
    const bold = text.slice(i).match(/^\*\*([^*]+)\*\*/);
    if (bold) {
      nodes.push(
        <Typography component="span" sx={{ fontWeight: 700, color: DESIGN_TOKENS.colors.text.primary }}>
          {bold[1]}
        </Typography>
      );
      i += bold[0].length;
      continue;
    }

    const italic = text.slice(i).match(/^\*([^*]+)\*/) || text.slice(i).match(/^_([^_]+)_/);
    if (italic) {
      nodes.push(
        <Typography component="span" sx={{ fontStyle: 'italic', color: DESIGN_TOKENS.colors.text.primary }}>
          {italic[1]}
        </Typography>
      );
      i += italic[0].length;
      continue;
    }

    const strike = text.slice(i).match(/^~~([^~]+)~~/);
    if (strike) {
      nodes.push(
        <Typography component="span" sx={{ textDecoration: 'line-through', color: DESIGN_TOKENS.colors.text.primary }}>
          {strike[1]}
        </Typography>
      );
      i += strike[0].length;
      continue;
    }

    const code = text.slice(i).match(/^`([^`]+)`/);
    if (code) {
      nodes.push(
        <Box
          component="span"
          sx={{
            fontFamily: 'monospace',
            bgcolor: 'action.hover',
            px: 0.75,
            py: 0.25,
            borderRadius: 0.75,
            color: DESIGN_TOKENS.colors.text.primary,
          }}
        >
          {code[1]}
        </Box>
      );
      i += code[0].length;
      continue;
    }

    const link = text.slice(i).match(/^\[([^\]]+)\]\(([^\)]+)\)/);
    if (link) {
      nodes.push(
        <Box
          component="a"
          href={link[2]}
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            color: DESIGN_TOKENS.colors.primary[700],
            textDecoration: 'underline',
            textUnderlineOffset: '3px',
          }}
        >
          {link[1]}
        </Box>
      );
      i += link[0].length;
      continue;
    }

    nodes.push(text[i]);
    i += 1;
  }
  return nodes;
}

function renderMarkdown(text: string, opts?: { headingIdPrefix?: string }): React.ReactNode {
  const parts: { type: 'code' | 'text'; content: string; lang?: string }[] = [];
  let pos = 0;
  while (pos < text.length) {
    const start = text.indexOf('```', pos);
    if (start === -1) {
      parts.push({ type: 'text', content: text.slice(pos) });
      break;
    }
    if (start > pos) parts.push({ type: 'text', content: text.slice(pos, start) });
    const end = text.indexOf('```', start + 3);
    const segment = end === -1 ? text.slice(start + 3) : text.slice(start + 3, end);
    const m = segment.match(/^([a-zA-Z0-9_-]+)\s*(?:\n|\s)?/);
    let lang: string | undefined = undefined;
    let code = segment;
    if (m) {
      lang = m[1];
      code = segment.slice(m[0].length);
    }
    parts.push({ type: 'code', content: code, lang });
    pos = end === -1 ? text.length : end + 3;
  }

  const normalizeMarkdownInline = (s: string): string => {
    return s.replace(/(\S)\s+(#{1,6})\s+/g, '$1\n$2 ');
  };

  const renderText = (block: string, keyPrefix: string) => {
    const lines = normalizeMarkdownInline(block).split(/\r?\n/);
    const nodes: React.ReactNode[] = [];
    let i = 0;

    while (i < lines.length) {
      const line = lines[i];
      if (!line.trim()) {
        i++;
        continue;
      }

      const h = line.match(/^\s*(#{1,6})\s+(.*)$/);
      if (h) {
        const level = h[1].length;
        const textLine = h[2];
        const id = `${opts?.headingIdPrefix ?? ''}${slugify(textLine)}`;
        const variant = level === 1 ? 'h5' : level === 2 ? 'h6' : 'subtitle1';
        const sx =
          level === 1
            ? { mt: 4, mb: 1.5, fontWeight: 800, fontSize: { xs: 20, md: 22 }, lineHeight: 1.2 }
            : level === 2
              ? { mt: 3.5, mb: 1.25, fontWeight: 800, fontSize: { xs: 16, md: 18 }, lineHeight: 1.25 }
              : { mt: 2.5, mb: 1, fontWeight: 800, fontSize: { xs: 14, md: 16 }, lineHeight: 1.3 };
        nodes.push(
          <Typography
            key={`${keyPrefix}-h-${i}`}
            id={id}
            variant={variant}
            sx={{ color: DESIGN_TOKENS.colors.text.primary, scrollMarginTop: '96px', ...sx }}
          >
            {renderInline(textLine)}
          </Typography>
        );
        i++;
        continue;
      }

      if (/^\s*(?:-{3,}|_{3,}|\*{3,})\s*$/.test(line)) {
        nodes.push(<Divider key={`${keyPrefix}-hr-${i}`} sx={{ my: 2 }} />);
        i++;
        continue;
      }

      if (/^\s*>\s?/.test(line)) {
        const items: string[] = [];
        while (i < lines.length && /^\s*>\s?/.test(lines[i])) {
          items.push(lines[i].replace(/^\s*>\s?/, ''));
          i++;
        }
        nodes.push(
          <Box
            key={`${keyPrefix}-q-${i}`}
            sx={{
              borderLeft: '3px solid',
              borderColor: DESIGN_TOKENS.colors.neutral[300],
              pl: 2,
              py: 1,
              my: 2,
              bgcolor: 'action.hover',
              borderRadius: 2,
            }}
          >
            {items.map((t, idx) => (
              <Typography key={`${keyPrefix}-q-${i}-${idx}`} variant="body1" sx={{ color: DESIGN_TOKENS.colors.text.primary }}>
                {renderInline(t)}
              </Typography>
            ))}
          </Box>
        );
        continue;
      }

      const ulMatch = line.match(/^\s*[-*]\s+(.*)$/);
      const olMatch = line.match(/^\s*(\d{1,3})\.\s+(.*)$/);

      if (ulMatch) {
        const items: string[] = [];
        while (i < lines.length) {
          const m2 = lines[i].match(/^\s*[-*]\s+(.*)$/);
          if (!m2) break;
          items.push(m2[1]);
          i++;
        }
        nodes.push(
          <Box key={`${keyPrefix}-ul-${i}`} sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, pl: 2, my: 1 }}>
            {items.map((t, idx) => (
              <Typography key={`${keyPrefix}-ul-${i}-${idx}`} variant="body1" sx={{ display: 'flex', gap: 1, color: DESIGN_TOKENS.colors.text.primary }}>
                <Box component="span">•</Box>
                <Box component="span" sx={{ flex: 1 }}>
                  {renderInline(t)}
                </Box>
              </Typography>
            ))}
          </Box>
        );
        continue;
      }

      if (olMatch) {
        const items: { n: string; t: string }[] = [];
        while (i < lines.length) {
          const m2 = lines[i].match(/^\s*(\d{1,3})\.\s+(.*)$/);
          if (!m2) break;
          items.push({ n: m2[1], t: m2[2] });
          i++;
        }
        nodes.push(
          <Box key={`${keyPrefix}-ol-${i}`} sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, pl: 2, my: 1 }}>
            {items.map((it, idx) => (
              <Typography key={`${keyPrefix}-ol-${i}-${idx}`} variant="body1" sx={{ display: 'flex', gap: 1, color: DESIGN_TOKENS.colors.text.primary }}>
                <Box component="span">{it.n}.</Box>
                <Box component="span" sx={{ flex: 1 }}>
                  {renderInline(it.t)}
                </Box>
              </Typography>
            ))}
          </Box>
        );
        continue;
      }

      const para: string[] = [];
      while (
        i < lines.length &&
        lines[i].trim() &&
        !/^\s*(#{1,6})\s+/.test(lines[i]) &&
        !/^\s*[-*]\s+/.test(lines[i]) &&
        !/^\s*\d{1,3}\.\s+/.test(lines[i]) &&
        !/^\s*>\s?/.test(lines[i])
      ) {
        para.push(lines[i].trim());
        i++;
      }

      nodes.push(
        <Typography
          key={`${keyPrefix}-p-${i}`}
          variant="body1"
          sx={{
            whiteSpace: 'pre-wrap',
            color: DESIGN_TOKENS.colors.text.primary,
            lineHeight: 1.7,
            fontSize: { xs: 15, md: 16 },
          }}
        >
          {renderInline(para.join(' '))}
        </Typography>
      );
    }

    return nodes;
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {parts.map((p, idx) => {
        if (p.type === 'code') {
          if ((p.lang || '').toLowerCase() === 'md' || (p.lang || '').toLowerCase() === 'markdown' || (p.lang || '').toLowerCase() === 'text') {
            return <Box key={`md-${idx}`}>{renderMarkdown(p.content, opts)}</Box>;
          }
          return (
            <Box
              key={`code-${idx}`}
              sx={{
                bgcolor: 'action.hover',
                border: `1px solid ${DESIGN_TOKENS.colors.neutral[200]}`,
                borderRadius: 2,
                p: 2,
              }}
            >
              <Typography component="pre" sx={{ m: 0, fontFamily: 'monospace', whiteSpace: 'pre-wrap', color: DESIGN_TOKENS.colors.text.primary }}>
                {p.content}
              </Typography>
            </Box>
          );
        }

        return <Box key={`text-${idx}`}>{renderText(p.content, `t-${idx}`)}</Box>;
      })}
    </Box>
  );
}

export const MarkdownRenderer: React.FC<{ text: string }> = ({ text }) => {
  return <>{renderMarkdown(text)}</>;
};

export const MarkdownRendererWithAnchors: React.FC<{ text: string; headingIdPrefix?: string }> = ({ text, headingIdPrefix }) => {
  return <>{renderMarkdown(text, { headingIdPrefix })}</>;
};
